import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  AssignmentReturn as AssignmentReturnIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility,
  Edit,
  Close,
  Person,
  Save,
  Add,
  Receipt,
  Cancel,
} from '@mui/icons-material';

const ReturnRefund = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    refundType: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    returnStatus: '',
    refundType: '',
    refundAmount: '',
    refundMethod: '',
    reason: '',
    notes: '',
  });

  const sampleData = [
    {
      sno: 1,
      returnId: 'RET001',
      orderId: 'ORD001',
      returnDate: '2025-01-01',
      customerName: 'Amit Gupta',
      customerPhone: '+91 98765 43210',
      productName: 'Samsung Galaxy S24',
      quantity: 1,
      returnAmount: 75000,
      returnStatus: 'Approved',
      refundType: 'Full Refund',
      refundMethod: 'Bank Transfer',
      reason: 'Product Defect',
      refundDate: '2025-01-03',
      notes: 'Screen flickering issue',
    },
    {
      sno: 2,
      returnId: 'RET002',
      orderId: 'ORD002',
      returnDate: '2025-01-02',
      customerName: 'Priya Sharma',
      customerPhone: '+91 98765 43211',
      productName: 'iPhone 15 Pro',
      quantity: 1,
      returnAmount: 120000,
      returnStatus: 'Pending',
      refundType: 'Partial Refund',
      refundMethod: 'Credit Card',
      reason: 'Wrong Size',
      refundDate: '',
      notes: 'Customer wants different color',
    },
    {
      sno: 3,
      returnId: 'RET003',
      orderId: 'ORD003',
      returnDate: '2025-01-03',
      customerName: 'Rajesh Kumar',
      customerPhone: '+91 98765 43212',
      productName: 'OnePlus 12',
      quantity: 2,
      returnAmount: 110000,
      returnStatus: 'Rejected',
      refundType: 'No Refund',
      refundMethod: '',
      reason: 'Customer Changed Mind',
      refundDate: '',
      notes: 'Return period expired',
    },
    {
      sno: 4,
      returnId: 'RET004',
      orderId: 'ORD004',
      returnDate: '2025-01-04',
      customerName: 'Sneha Patel',
      customerPhone: '+91 98765 43213',
      productName: 'Google Pixel 8',
      quantity: 1,
      returnAmount: 65000,
      returnStatus: 'Processed',
      refundType: 'Full Refund',
      refundMethod: 'UPI',
      reason: 'Damaged in Transit',
      refundDate: '2025-01-05',
      notes: 'Package was damaged during delivery',
    },
    {
      sno: 5,
      returnId: 'RET005',
      orderId: 'ORD005',
      returnDate: '2025-01-05',
      customerName: 'Vikram Singh',
      customerPhone: '+91 98765 43214',
      productName: 'Xiaomi 14',
      quantity: 1,
      returnAmount: 45000,
      returnStatus: 'Under Review',
      refundType: 'Exchange',
      refundMethod: '',
      reason: 'Not as Described',
      refundDate: '',
      notes: 'Product specifications mismatch',
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Here you would typically make an API call to update the return/refund
    console.log('Saving return/refund:', selectedReturn?.returnId, editFormData);
    
    // For demo purposes, we'll just close the dialog
    // In a real app, you'd update the return in your state/API
    setEditDialogOpen(false);
    setSelectedReturn(null);
    setEditFormData({
      returnStatus: '',
      refundType: '',
      refundAmount: '',
      refundMethod: '',
      reason: '',
      notes: '',
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      status: filters.status,
      refundType: filters.refundType
    }); // Debug log
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (returnItem) => {
    setSelectedReturn(returnItem);
    setViewDialogOpen(true);
  };

  const handleEdit = (returnItem) => {
    setSelectedReturn(returnItem);
    setEditFormData({
      returnStatus: returnItem.returnStatus,
      refundType: returnItem.refundType,
      refundAmount: returnItem.returnAmount.toString(),
      refundMethod: returnItem.refundMethod,
      reason: returnItem.reason,
      notes: returnItem.notes,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedReturn(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedReturn(null);
    setEditFormData({
      returnStatus: '',
      refundType: '',
      refundAmount: '',
      refundMethod: '',
      reason: '',
      notes: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return sampleData;
    }

    let filteredData = [...sampleData];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.returnId.toLowerCase().includes(searchLower) ||
        item.orderId.toLowerCase().includes(searchLower) ||
        item.customerName.toLowerCase().includes(searchLower) ||
        item.productName.toLowerCase().includes(searchLower) ||
        item.reason.toLowerCase().includes(searchLower)
      );
    }

    // Apply date filters
    if (filters.fromDate) {
      filteredData = filteredData.filter(item => new Date(item.returnDate) >= new Date(filters.fromDate));
    }
    if (filters.toDate) {
      filteredData = filteredData.filter(item => new Date(item.returnDate) <= new Date(filters.toDate));
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => item.returnStatus === filters.status);
    }

    // Apply refund type filter
    if (filters.refundType) {
      filteredData = filteredData.filter(item => item.refundType === filters.refundType);
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.fromDate, filters.toDate, filters.status, filters.refundType]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalReturns = filteredData.length;
  const approvedReturns = filteredData.filter(item => item.returnStatus === 'Approved' || item.returnStatus === 'Processed').length;
  const pendingReturns = filteredData.filter(item => item.returnStatus === 'Pending' || item.returnStatus === 'Under Review').length;
  const rejectedReturns = filteredData.filter(item => item.returnStatus === 'Rejected').length;
  const totalRefundAmount = filteredData.reduce((sum, item) => sum + item.returnAmount, 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Processed': return 'info';
      case 'Pending': return 'warning';
      case 'Under Review': return 'primary';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getRefundTypeColor = (type) => {
    switch (type) {
      case 'Full Refund': return 'success';
      case 'Partial Refund': return 'warning';
      case 'Exchange': return 'info';
      case 'No Refund': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Return & Refund Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Process and manage product returns and refunds
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Return Request
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <AssignmentReturnIcon sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {totalReturns}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Returns
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {approvedReturns}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Approved
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <AttachMoneyIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                ₹{(totalRefundAmount / 100000).toFixed(2)}L
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Refund Amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <ScheduleIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {pendingReturns}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Pending
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
            Return & Refund Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Return Status</InputLabel>
                <Select 
                  label="Return Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Under Review">Under Review</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Processed">Processed</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Refund Type</InputLabel>
                <Select 
                  label="Refund Type" 
                  value={filters.refundType}
                  onChange={(e) => {
                    setFilters({ ...filters, refundType: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Full Refund">Full Refund</MenuItem>
                  <MenuItem value="Partial Refund">Partial Refund</MenuItem>
                  <MenuItem value="Exchange">Exchange</MenuItem>
                  <MenuItem value="No Refund">No Refund</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Returns"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by return ID, order ID, customer name, or product..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchLoading ? (
                        <CircularProgress size={20} color="primary" />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Search Controls */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Apply all filters and search" arrow>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  fromDate: '',
                  toDate: '',
                  status: '',
                  refundType: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f0f7ff',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e3f2fd' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e3f2fd',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching return records...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} return{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.refundType && filters.refundType !== 'All Types' && (
                      <Chip
                        label={`Type: ${filters.refundType}`} 
                        size="small"
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.fromDate && (
                      <Chip 
                        label={`From: ${filters.fromDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.toDate && (
                      <Chip 
                        label={`To: ${filters.toDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Return & Refund Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Return ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Order ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Product</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Refund Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Reason</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching return records...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.sno} 
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.returnId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.orderId}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.customerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customerPhone}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.productName}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{row.returnAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.returnStatus} 
                          size="small"
                          color={getStatusColor(row.returnStatus)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.refundType} 
                          size="small"
                          color={getRefundTypeColor(row.refundType)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.reason}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Return">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No return records found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentReturnIcon />
            <Typography variant="h6" fontWeight="bold">
              Return & Refund Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedReturn && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedReturn.returnId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Order: {selectedReturn.orderId} | Customer: {selectedReturn.customerName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedReturn.returnStatus} 
                      size="small" 
                      color={getStatusColor(selectedReturn.returnStatus)}
                    />
                    <Chip 
                      label={selectedReturn.refundType} 
                      size="small" 
                      color={getRefundTypeColor(selectedReturn.refundType)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Return Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentReturnIcon color="primary" />
                        Return Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Return ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.returnId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.orderId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Return Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.returnDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Product:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.productName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.quantity}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Return Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">₹{selectedReturn.returnAmount.toLocaleString()}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Refund Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt color="primary" />
                        Refund Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Refund Type:</Typography>
                          <Chip 
                            label={selectedReturn.refundType} 
                            size="small" 
                            color={getRefundTypeColor(selectedReturn.refundType)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Refund Method:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.refundMethod || 'Not specified'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Refund Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.refundDate || 'Not processed'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Return Status:</Typography>
                          <Chip 
                            label={selectedReturn.returnStatus} 
                            size="small" 
                            color={getStatusColor(selectedReturn.returnStatus)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Reason:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReturn.reason}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Details */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="primary" />
                    Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Customer Name:</strong> {selectedReturn.customerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Phone:</strong> {selectedReturn.customerPhone}
                      </Typography>
                    </Grid>
                    {selectedReturn.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Notes:</strong> {selectedReturn.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Return Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#ff9800',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            <Typography variant="h6" fontWeight="bold">
              Edit Return/Refund
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedReturn && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Edit return/refund details for: <strong>{selectedReturn.returnId}</strong>
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Return Status</InputLabel>
                    <Select
                      value={editFormData.returnStatus}
                      onChange={(e) => handleEditFormChange('returnStatus', e.target.value)}
                      label="Return Status"
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Under Review">Under Review</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Processed">Processed</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Refund Type</InputLabel>
                    <Select
                      value={editFormData.refundType}
                      onChange={(e) => handleEditFormChange('refundType', e.target.value)}
                      label="Refund Type"
                    >
                      <MenuItem value="Full Refund">Full Refund</MenuItem>
                      <MenuItem value="Partial Refund">Partial Refund</MenuItem>
                      <MenuItem value="Exchange">Exchange</MenuItem>
                      <MenuItem value="No Refund">No Refund</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Refund Amount"
                    type="number"
                    value={editFormData.refundAmount}
                    onChange={(e) => handleEditFormChange('refundAmount', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Refund Method</InputLabel>
                    <Select
                      value={editFormData.refundMethod}
                      onChange={(e) => handleEditFormChange('refundMethod', e.target.value)}
                      label="Refund Method"
                    >
                      <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                      <MenuItem value="Credit Card">Credit Card</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason</InputLabel>
                    <Select
                      value={editFormData.reason}
                      onChange={(e) => handleEditFormChange('reason', e.target.value)}
                      label="Reason"
                    >
                      <MenuItem value="Product Defect">Product Defect</MenuItem>
                      <MenuItem value="Wrong Size">Wrong Size</MenuItem>
                      <MenuItem value="Damaged in Transit">Damaged in Transit</MenuItem>
                      <MenuItem value="Not as Described">Not as Described</MenuItem>
                      <MenuItem value="Customer Changed Mind">Customer Changed Mind</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editFormData.notes}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              {/* Read-only fields */}
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Return Information (Read-only)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Return ID:</strong> {selectedReturn.returnId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Order ID:</strong> {selectedReturn.orderId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Customer:</strong> {selectedReturn.customerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Product:</strong> {selectedReturn.productName}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReturnRefund;
